import { Button, ProfilePicture } from "@sellar-io/components";
import { useRef, useState } from "react";
import { useIntercom } from "react-use-intercom";
import { useMediaQuery } from "usehooks-ts";
import { twMerge } from "tailwind-merge";
import { useSellarUser } from "../../../hooks/useSellarUser.shared";
import { useSellarCurrentBusiness } from "../../../hooks/useSellarCurrentBusiness.shared";
import { useUser } from "../../../hooks/api/v2/user/useUser";
import { NavigationOverlay } from "./NavigationOverlay";
import { useNavigationOverlay } from "./NavigationOverlayContext.shared";
import { AccountNavigationContent } from "./AccountNavigationContent";
import { SwitchBusinessNavigationContent } from "./SwitchBusinessNavigationContent";

type AccountNavigationOverlayProps = {
  className?: string;
};

export const AccountNavigationOverlay = (
  props: AccountNavigationOverlayProps,
) => {
  const { user } = useSellarUser();
  if (!user) {
    return null;
  }
  return <AccountNavigationOverlayWithUser {...props} userId={user.id} />;
};

const AccountNavigationOverlayWithUser = ({
  className,
  userId,
}: AccountNavigationOverlayProps & { userId: number }) => {
  const { user, signOut } = useSellarUser();
  const { business } = useSellarCurrentBusiness();
  const profile = useUser(userId);
  const { activeOverlay, closeOverlay } = useNavigationOverlay();
  const intercom = useIntercom();
  const isMobile = useMediaQuery("(max-width: 640px)");
  const [switching, setSwitching] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const signOutPending = useRef(false);
  const [signOutFailed, setSignOutFailed] = useState(false);
  if (switching && (activeOverlay !== "account" || !isMobile)) {
    setSwitching(false);
  }
  const close = () => closeOverlay("account");
  const handleSignOut = async () => {
    if (signOutPending.current) {
      return;
    }
    signOutPending.current = true;
    setSigningOut(true);
    setSignOutFailed(false);
    try {
      await signOut();
      close();
    } catch {
      setSignOutFailed(true);
    } finally {
      signOutPending.current = false;
      setSigningOut(false);
    }
  };
  const name = profile.data?.name || user?.name || "Your account";
  return (
    <NavigationOverlay
      id="account"
      title={switching ? "Switch business" : "Account"}
      mobilePresentation="panel"
      trigger={(props) => (
        <Button
          {...props}
          color="transparent"
          aria-label={`Account: ${name}`}
          className={twMerge(
            "inline-flex size-8 shrink-0 items-center justify-center rounded-full p-0 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-purple-600",
            className,
          )}
        >
          <ProfilePicture
            logoUrl={profile.data?.profileImageUrl}
            name={name}
            className="size-8 max-h-8 min-h-8 max-w-8 min-w-8"
          />
        </Button>
      )}
    >
      {switching ? (
        <SwitchBusinessNavigationContent
          onBack={() => setSwitching(false)}
          onClose={close}
        />
      ) : (
        <AccountNavigationContent
          name={name}
          email={user?.email || undefined}
          imageUrl={profile.data?.profileImageUrl}
          businessName={business?.name}
          companyName={
            business?.BusinessLegalEntity?.name ||
            business?.registeredCompanyName
          }
          onSwitchBusiness={() => setSwitching(true)}
          onClose={close}
          onHelp={() => {
            close();
            intercom.show();
          }}
          onSignOut={() => {
            handleSignOut();
          }}
          signingOut={signingOut}
          signOutFailed={signOutFailed}
          loading={profile.isLoading}
          profileFailed={profile.isError}
          onRetryProfile={() => {
            profile.refetch();
          }}
        />
      )}
    </NavigationOverlay>
  );
};
